import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Input } from "barte-design-system";
import { FloppyDiskIcon, InfoIcon } from "@phosphor-icons/react";
import { useValidationErrors } from "@payfac/hooks";

import { objectShape } from "./modal-confirm-save.constants";
import styles from "./modal-confirm-save.module.css";
import type { ModalConfirmSaveProps } from "./modal-confirm-save.type";

export const ModalConfirmSave = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
}: ModalConfirmSaveProps) => {
    const { t } = useTranslation();
    const [description, setDescription] = useState("");
    const [showErrors, setShowErrors] = useState(false);

    const { isValid, errors, onHandleListenErrors } = useValidationErrors(
        objectShape(t),
        { description },
    );

    useEffect(() => {
        if (!isOpen) {
            setDescription("");
            setShowErrors(false);
        }
    }, [isOpen]);

    const handleSave = () => {
        if (!isValid) {
            setShowErrors(true);
            onHandleListenErrors(true);
            return;
        }

        onConfirm(description);
    };

    const title = (
        <div className={styles["modal-header"]}>
            <FloppyDiskIcon weight="regular" /> {t("payfac.pageRatePlanModalConfirmSaveTitle")}
        </div>
    ) as unknown as string;

    const observationLabel = (
        <div className={styles["observation-label"]}>
            <InfoIcon weight="regular" /> {t("payfac.pageRatePlanModalConfirmSaveLabel")}
        </div>
    ) as unknown as string;

    return (
        <Modal
            className={styles.modal}
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="sm"
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalConfirmSaveButton"),
                    onClick: handleSave,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <p className={styles.description}>
                    {t("payfac.pageRatePlanModalConfirmSaveDescription")}
                </p>

                <div className={styles.field}>
                    <Input
                        type="textarea"
                        label={observationLabel}
                        placeholder={t("payfac.pageRatePlanModalConfirmSavePlaceholder")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isLoading}
                        rows={5}
                        error={showErrors && !!errors?.description}
                        helpText={showErrors ? errors?.description : ""}
                    />
                </div>
            </div>
        </Modal>
    );
};
